import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Container } from "@mui/material";
import { pathOr, range } from "ramda";
import useVisibilitySensor from "@rooks/use-visibility-sensor";
import { Navigation, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useHover } from "hooks";
import withAlgoliaTracking from "components/withAlgoliaTracking";
import StarringYouClip from "components/StarringYouClip";
import StarringYouClipPresenter from "presenters/StarringYouClipPresenter";
import { jjLogger } from "utils/logUtils";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import useStyles from "./useStyles";

const StarringYouClipAlgolia = withAlgoliaTracking(StarringYouClip);

const StarringYouClipsCarousel = ({ items, animated, breakpoints, loading, showVideo }) => {
  const classes = useStyles();
  const sliderContainerRef = useRef(null);
  const swiperRef = useRef(null);
  const [ref, isHovered] = useHover();
  const { isVisible } = useVisibilitySensor(sliderContainerRef, {
    resizeCheck: true,
    partialVisibility: "top",
    minTopValue: pathOr(0, ["current", "offsetHeight"], swiperRef) / 2,
  });

  const animatedThumbs = items ? items.filter(StarringYouClipPresenter.animatedThumb) : [];
  const staticThumbs = items ? items.filter(StarringYouClipPresenter.thumb) : [];
  const thumbnails = animated ? animatedThumbs : staticThumbs;

  useEffect(() => {
    if (isVisible && !isHovered && !loading) {
      swiperRef.current.swiper.autoplay.start();
    } else {
      swiperRef.current.swiper.autoplay.stop();
    }
  }, [isVisible, isHovered, loading]);

  const renderSlide = (isActive, item, isSliceVisible, isPrev, isNext) => {
    return (
      <div
        data-testid={`swiperSlide-${animated ? "animated" : "static"}`}
        className={classes.slide}
        key={StarringYouClipPresenter.id(item)}
      >
        <StarringYouClipAlgolia
          showVideo={showVideo}
          item={item}
          isActive={isActive}
          playing={animated && isVisible && (isSliceVisible || isPrev || isNext)}
        />
      </div>
    );
  };

  const renderContent = () => {
    if (loading)
      return range(0, 2).map((index) => (
        <div className={classes.slide} key={`StarringYouClipsCarousel-placeholder-${index}`}>
          <SwiperSlide key={`StarringYouClipsCarousel-swiperslide-${index}`}>
            <StarringYouClip loading />
          </SwiperSlide>
        </div>
      ));

    return (
      <>
        {thumbnails &&
          thumbnails.map((item) => {
            return (
              <SwiperSlide key={StarringYouClipPresenter.id(item)}>
                {({ isActive, isVisible: isSliceVisible, isPrev, isNext }) =>
                  renderSlide(isActive, item, isSliceVisible, isPrev, isNext)
                }
              </SwiperSlide>
            );
          })}
      </>
    );
  };

  jjLogger.logDebug("StarringYouClipsCarousel.js: Rendering");
  jjLogger.logDebug("StarringYouClipsCarousel.js: loading");
  jjLogger.logDebug(loading);
  jjLogger.logDebug("StarringYouClipsCarousel.js: isVisible");
  jjLogger.logDebug(isVisible);
  jjLogger.logDebug("StarringYouClipsCarousel.js: isHovered");
  jjLogger.logDebug(isHovered);

  return (
    <div className={classes.root}>
      <Container ref={ref}>
        <div ref={sliderContainerRef} className={classes.sliderWrapper}>
          <Swiper
            ref={swiperRef}
            slidesPerView={1}
            modules={[Navigation, Autoplay]}
            loop
            watchSlidesProgress
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            navigation
            breakpoints={breakpoints}
            observer
            observeParents
          >
            {renderContent()}
          </Swiper>
        </div>
      </Container>
    </div>
  );
};

StarringYouClipsCarousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      attributes: PropTypes.shape({}),
    }).isRequired,
  ),
  breakpoints: PropTypes.shape({}),
  animated: PropTypes.bool,
  loading: PropTypes.bool,
  showVideo: PropTypes.bool,
};

StarringYouClipsCarousel.defaultProps = {
  showVideo: true,
  animated: true,
  loading: false,
  breakpoints: {
    0: {
      slidesPerView: 1.1,
      spaceBetween: 16,
    },
    650: {
      slidesPerView: 1.6,
      spaceBetween: 16,
    },
  },
};

export default StarringYouClipsCarousel;
